import clsx from 'clsx'
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner'
import UploadingTracks from '../../components/UploadingTracks/UploadingTrack'
import TrackList from '../../components/TrackList/TrackList'
import { FileUploader } from 'react-drag-drop-files'
import PaginationBar from '../../components/PaginationBar/PaginationBar'
import { useContext, useEffect, useRef, useState } from 'react'
import {
  apiSlice,
  useDeleteTracksMutation,
  useTracksQuery,
} from '../../api/api'
import { useDispatch } from 'react-redux'
import { ReactComponent as MusicIcon } from '../../assets/icons/music.svg'
import './tracks-view.scss'
import IconMenu from '../../components/IconMenu/IconMenu'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg'
import { ReactComponent as CaretDownIcon } from 'assets/icons/caret-down.svg'
import GeneralModal from '../../components/GeneralModal/GeneralModal'
import LoadingBlocker from '../../components/LoadingSpinner/LoadingBlocker'
import { UploadContext } from '../../contexts/UploadContext'
import useIsSubscribed from '../../utils/hooks/useIsSubscribed'

const TracksView = ({ onEditTracks, lockSelections }) => {
  const dispatch = useDispatch()
  const uploadContext = useContext(UploadContext)
  const isSubscribed = useIsSubscribed()

  const ref = useRef(null)
  const newTrackRef = useRef(null)

  const [selectedTracks, setSelectedTracks] = useState([])
  const [isDroppingFile, setIsDroppingFile] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(
    window.localStorage.getItem('trackSort') || null,
  )
  const [showMaxTracksModal, setShowMaxTracksModal] = useState(false)

  const [tracksToDelete, setTracksToDelete] = useState([])

  const { data: tracksData, isLoading, isFetching } = useTracksQuery()
  const [deleteTracks, { isLoading: deleteLoading }] = useDeleteTracksMutation()

  const { data: tracks, total, limit } = tracksData || {}
  const hasPendingTrack = tracksData?.data?.find(
    (track) => track.fileConversionResult === 'converting',
  )
  const searchFields = [
    'name',
    'artist',
    'album',
    'albumArtist',
    'composer',
    'genre',
    'originalFilename',
  ]
  const searchedTracks =
    tracks?.filter(
      (track) =>
        !search ||
        !!searchFields.find(
          (field) =>
            track[field].toLowerCase().indexOf(search.toLowerCase()) > -1,
        ),
    ) || []
  const sortedTracks = sortTracks(searchedTracks, sort)
  const disableUpload = !isSubscribed && tracks?.length > 2

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasPendingTrack) {
        dispatch(apiSlice.util.invalidateTags([{ type: 'Tracks', id: 'LIST' }]))
      }
    }, 15000)

    return () => clearInterval(interval)
  }, [tracksData])

  useEffect(() => {
    const clickEvent = (evt) => {
      if (!ref.current.contains(evt.target) && !lockSelections) {
        setSelectedTracks([])
      }
    }

    if (ref.current) {
      document.addEventListener('click', clickEvent)
    }
    return () => document.removeEventListener('click', clickEvent)
  }, [ref, lockSelections])

  useEffect(() => {
    const keydownEvent = (evt) => {
      const activeElement = document.activeElement
      const notInput =
        activeElement.tagName !== 'INPUT' &&
        activeElement.tagName !== 'TEXTAREA' &&
        document.activeElement.contentEditable !== 'true'
      if (
        selectedTracks.length > 0 &&
        notInput &&
        (evt.key === 'Delete' || evt.key === 'Backspace')
      ) {
        setTracksToDelete(selectedTracks)
      }
    }

    document.addEventListener('keydown', keydownEvent)

    return () => {
      document.removeEventListener('keydown', keydownEvent)
    }
  }, [ref, JSON.stringify(selectedTracks)])

  useEffect(() => {
    setSelectedTracks(
      selectedTracks.filter(
        (track) => !!sortedTracks.find((search) => search.id === track.id),
      ),
    )
  }, [search])

  function handlePageChange(newPage) {
    setPage(newPage)
  }

  function handleFileUpload(files) {
    if (!isSubscribed) {
      const spaceAvailable = 3 - tracks.length
      if (spaceAvailable > 0) {
        uploadTracks([...files].slice(0, spaceAvailable))
      } else {
        setShowMaxTracksModal(true)
      }
    } else {
      uploadTracks([...files])
    }
    setIsDroppingFile(false)
  }

  function handleNewTrack(evt) {
    uploadTracks([...evt.target.files])
    evt.target.value = null
  }

  function uploadTracks(files) {
    uploadContext.uploadTracks(dispatch, files)
  }

  function handleDragOver(evt) {
    if (evt?.dataTransfer?.types?.[0] === 'Files') {
      setIsDroppingFile(true)
    }
  }

  function handleTrackClick(evt, track) {
    if (evt.shiftKey) {
      const lastSelectedTrack = [...selectedTracks].pop()
      const trackIndex = sortedTracks.findIndex((item) => item.id === track.id)
      const lastIndex = sortedTracks.findIndex(
        (item) => item.id === lastSelectedTrack.id,
      )
      if (trackIndex < lastIndex) {
        setSelectedTracks([
          ...selectedTracks,
          ...sortedTracks
            .slice(trackIndex, lastIndex)
            .filter(
              (item) => !selectedTracks.find((search) => search.id === item.id),
            ),
        ])
      } else if (trackIndex > lastIndex) {
        setSelectedTracks([
          ...selectedTracks,
          ...sortedTracks
            .slice(lastIndex, trackIndex + 1)
            .filter(
              (item) => !selectedTracks.find((search) => search.id === item.id),
            ),
        ])
      }
    } else {
      if (evt.ctrlKey || evt.metaKey) {
        if (selectedTracks.find((item) => item.id === track.id)) {
          setSelectedTracks(
            selectedTracks.filter((item) => item.id !== track.id),
          )
        } else {
          setSelectedTracks([...selectedTracks, track])
        }
      } else {
        setSelectedTracks([track])
      }
    }
  }

  function handleTrackDoubleClick(track) {
    onEditTracks([track])
  }

  function handleSortChange(newSort) {
    window.localStorage.setItem('trackSort', newSort)
    setSort(newSort)
  }

  function handleEditClick() {
    const currentTracks = tracks.filter(
      (track) => !!selectedTracks.find((search) => search.id === track.id),
    )
    onEditTracks(currentTracks)
  }

  function handleDeleteTracks() {
    deleteTracks(selectedTracks.map((track) => track.id)).then(() => {
      setSelectedTracks([])
      setTracksToDelete([])
    })
  }

  return (
    <div className={clsx('tracks-view w-full h-full relative ')} ref={ref}>
      <div
        className={clsx('h-full w-full flex flex-col', {
          'pointer-events-none': lockSelections,
        })}
        onDragOver={handleDragOver}
        onDragEnd={() => setIsDroppingFile(false)}
        onDrop={() => setIsDroppingFile(false)}
      >
        <div className="flex items-center px-6 py-4">
          <div className="flex-1">
            <div className="flex">
              <SortDropdown value={sort} onChange={handleSortChange} />
            </div>
          </div>
          <div className="search">
            <input
              type="text"
              className="input-sm"
              placeholder="Search tracks..."
              onChange={(evt) => setSearch(evt.target.value)}
            />
          </div>
        </div>

        <div className="actions px-6 ">
          {selectedTracks.length === 0 ? (
            <div className="buttons">
              <button
                type="button"
                className="button bg-blue hover:bg-blue-med"
                disabled={disableUpload}
                onClick={() => newTrackRef.current.click()}
                {...(disableUpload
                  ? {
                      'data-tooltip':
                        'Free accounts are limited to three track uploads.',
                    }
                  : {})}
              >
                Upload Track
              </button>
            </div>
          ) : (
            <div className="flex items-center">
              {selectedTracks.length > 0 && (
                <>
                  <button
                    className="button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark"
                    type="button"
                    key={'unique-button'}
                    onClick={() => setSelectedTracks([])}
                  >
                    <CloseIcon
                      className="fill-purple dark:fill-white"
                      height="24"
                      width="24"
                    />
                  </button>
                  <div className="flex items-center leading-4 ml-2">
                    {selectedTracks.length} selected
                  </div>
                </>
              )}
              <>
                <button
                  className="button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark ml-8"
                  onClick={handleEditClick}
                  data-tooltip={`Edit Track${
                    selectedTracks.length > 1 ? 's' : ''
                  }`}
                >
                  <EditIcon
                    className="fill-purple dark:fill-white"
                    height="18"
                    width="18"
                  />
                </button>
                <button
                  className="button icon bg-white-purple hover:bg-white dark:bg-gray-light dark:hover:bg-gray-dark ml-2"
                  data-tooltip={`Delete Track${
                    selectedTracks.length > 1 ? 's' : ''
                  }`}
                  onClick={() => setTracksToDelete(selectedTracks)}
                >
                  <DeleteIcon
                    className="fill-purple dark:fill-white"
                    height="18"
                    width="18"
                  />
                </button>
              </>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex-grow-0 overflow-auto px-6 pb-1">
            <UploadingTracks />
            <TrackList
              tracks={sortedTracks}
              onTrackClick={handleTrackClick}
              onTrackDoubleClick={handleTrackDoubleClick}
              selectedTracks={selectedTracks}
              search={search}
              isLoading={isFetching}
            />
          </div>
        )}
      </div>
      <FileUploader
        multiple={true}
        disabled={true}
        classes={`file-uploader ${isDroppingFile ? 'dropping-file' : ''}`}
        dropMessageStyle={{ display: 'none' }}
        handleChange={handleFileUpload}
        onDraggingStateChange={(dragging) => setIsDroppingFile(dragging)}
        name="file"
      >
        <div>
          <div className="flex justify-center items-center">
            <MusicIcon fill="#d63b5c" height="100" width="100" />
          </div>
          <h3 className="text-pink text-2xl">Drop tracks to upload.</h3>
        </div>
      </FileUploader>
      {total > limit && (
        <PaginationBar
          total={total}
          page={page}
          onChange={handlePageChange}
          limit={limit}
        />
      )}

      <input
        className="hidden"
        type="file"
        accept=".flac,audio/*"
        onChange={handleNewTrack}
        ref={newTrackRef}
      />

      <GeneralModal
        isOpen={tracksToDelete.length > 0}
        onRequestClose={() => {
          setTimeout(() => setSelectedTracks(tracksToDelete), 0)
          setTracksToDelete([])
        }}
        title="Delete Tracks"
        buttons={[
          {
            className: 'button bg-red',
            text: 'Delete',
            onClick: handleDeleteTracks,
          },
        ]}
      >
        <div>
          {tracksToDelete.length > 1 ? (
            <div>
              Are you sure you want to delete the following tracks:
              <ul className="mt-2 mb-2">
                {tracksToDelete.map((track) => (
                  <li className="font-bold mt-2" key={track.id}>
                    <h4>{track.name}</h4>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div>
              Are you sure you want to delete{' '}
              <span className="font-bold">{tracksToDelete[0]?.name}</span>?
            </div>
          )}
          {deleteLoading && <LoadingBlocker />}
        </div>
      </GeneralModal>

      <GeneralModal
        isOpen={showMaxTracksModal}
        onRequestClose={() => setShowMaxTracksModal(false)}
        hideCancel
        title="Maximum Tracks Reached"
        buttons={[
          {
            className: 'button bg-transparent',
            text: 'Close',
            onClick: () => setShowMaxTracksModal(false),
          },
        ]}
      >
        <p>
          Free accounts are only able to upload three tracks. Upgrade to our
          professional plan to gain unlimited uploads.
        </p>
      </GeneralModal>
    </div>
  )
}

const SortDropdown = ({ value, onChange }) => {
  const sorts = [
    {
      id: 'alpha',
      name: 'Alphabetical (A-Z)',
    },
    {
      id: 'alphaReverse',
      name: 'Alphabetical (Z-A)',
    },
    {
      id: 'upload',
      name: 'Latest Upload',
    },
    {
      id: 'uploadReverse',
      name: 'Oldest Upload',
    },
    {
      id: 'modified',
      name: 'Last Modified',
    },
  ]
  const currentSort = sorts.find((sort) => sort.id === value)
  const sortedButton = (
    <div
      className={`font-bold text-xs rounded-l py-2 px-6 transition 
    border-solid bg-white border-purple-light border text-purple-dark hover:bg-white-light
    dark:border-0 dark:text-white dark:bg-blue-dark dark:hover:bg-blue-black`}
    >
      {currentSort?.name}
    </div>
  )
  const defaultButton = (
    <div className="font-bold text-xs rounded h-[32px] pl-6 pr-5 bg-gray-light border border-gray-lighter border-solid flex items-center">
      <span className="inline-block mr-1">Sort By</span>
      <CaretDownIcon />
    </div>
  )
  const dropdownButton = currentSort ? sortedButton : defaultButton
  return (
    <div className="flex">
      <IconMenu icon={dropdownButton} menuPosition="right">
        {sorts.map((sort) => (
          <button onClick={() => onChange(sort.id)} key={sort.id}>
            <span className="inline-block px-2 py-2">{sort.name}</span>
          </button>
        ))}
      </IconMenu>
      {currentSort && (
        <button
          className="font-bold text-xs rounded-r transition  bg-purple-light text-white dark:bg-blue-dark dark:hover:bg-blue-black"
          onClick={() => onChange(null)}
        >
          <CloseIcon fill="#fff" height="20" />
        </button>
      )}
    </div>
  )
}

function sortTracks(tracks, sort) {
  const sorts = {
    alpha: (a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1
      }
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1
      }
      return 0
    },
    alphaReverse: (a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return 1
      }
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return -1
      }
      return 0
    },
    upload: (a, b) => {
      if (new Date(a.createdAt).getTime() < new Date(b.createdAt).getTime()) {
        return 1
      }
      if (new Date(a.createdAt).getTime() > new Date(b.createdAt).getTime()) {
        return -1
      }
      return 0
    },
    uploadReverse: (a, b) => {
      if (new Date(a.createdAt).getTime() < new Date(b.createdAt).getTime()) {
        return -1
      }
      if (new Date(a.createdAt).getTime() > new Date(b.createdAt).getTime()) {
        return 1
      }
      return 0
    },
    modified: (a, b) => {
      if (new Date(a.updatedAt).getTime() < new Date(b.updatedAt).getTime()) {
        return 1
      }
      if (new Date(a.updatedAt).getTime() > new Date(b.updatedAt).getTime()) {
        return -1
      }
      return 0
    },
  }
  if (sort) {
    return [...(tracks || [])].sort(sorts[sort])
  }
  return tracks
}

export default TracksView
